import { Controller } from "@hotwired/stimulus"

// 계약서 양식 본문 편집기 — lit-monaco-editor 의 <lm-editor> + 나란히 보는 미리보기.
//
// 세 가지를 지킨다 (#184).
//
// 1. 편집 수단이 사라지지 않는다.
//    에디터 번들은 6MB 라 느리게 뜨거나 실패할 수 있다. 그래서 textarea 를 먼저 보여주고,
//    에디터가 실제로 준비된 뒤에만 자리를 바꾼다. 실패하면 textarea 그대로 쓰면 된다.
//
// 2. 작성한 내용이 날아가지 않는다.
//    값은 웹 컴포넌트 안에 있으므로 타이핑할 때마다, 그리고 제출 직전에 hidden 필드로 옮긴다.
//    검증에 걸려 서버가 폼을 다시 그려도 그 값이 다시 실려 오므로 이어서 고칠 수 있다.
//
// 3. 미리보기가 실제 계약서와 같은 규칙으로 그려진다.
//    스타일 토큰 치환은 서버 상수(StaffHelper::INPUT_STYLE_TOKENS)를 그대로 받아 쓴다.
//    미리보기가 자기 사본을 갖는 순간 "에디터에서는 노란 칸인데 실제 계약서는 회색"이 된다.
export default class extends Controller {
  static targets = ["field", "fallback", "editorBox", "preview", "previewStatus"]
  static values = {
    styleTokens: { type: Object, default: {} },
    stylesheetHint: { type: String, default: "" },
  }

  static EDITOR_SCRIPT_PATH = "/lit_monaco_editor/index.js"
  static PREVIEW_DEBOUNCE_MS = 300

  connect() {
    this.editorElement = null
    this.previewTimer = null

    // 에디터가 못 뜨더라도 제출은 항상 올바른 값을 싣는다
    this.handleSubmit = this.syncFieldValue.bind(this)
    this.element.closest("form")?.addEventListener("submit", this.handleSubmit)

    this.fallbackTarget.addEventListener("input", () => {
      if (!this.editorElement) {
        this.fieldTarget.value = this.fallbackTarget.value
        this.schedulePreview()
      }
    })

    this.fieldTarget.value = this.fallbackTarget.value

    this.renderPreview()
    this.mountEditor()
  }

  disconnect() {
    this.element.closest("form")?.removeEventListener("submit", this.handleSubmit)
    if (this.previewTimer) clearTimeout(this.previewTimer)
  }

  async mountEditor() {
    try {
      await import(this.constructor.EDITOR_SCRIPT_PATH)
      await customElements.whenDefined("lm-editor")
    } catch (error) {
      // textarea 가 그대로 남아 있으므로 편집은 계속할 수 있다
      console.warn("[contract-template-editor] 에디터를 불러오지 못해 textarea 로 편집합니다", error)
      return
    }

    const editor = document.createElement("lm-editor")
    editor.setAttribute("lang", "html")
    editor.setAttribute("disabled-footer", "true")
    editor.value = this.fallbackTarget.value
    editor.style.display = "block"
    editor.style.height = "100%"

    this.editorBoxTarget.appendChild(editor)
    this.editorElement = editor

    // 타이핑할 때마다 hidden 필드를 맞춰 둔다 — 제출이 언제 일어나도 값이 맞다
    editor.addEventListener("change", () => {
      this.syncFieldValue()
      this.schedulePreview()
    })

    this.fallbackTarget.classList.add("hidden")
    this.editorBoxTarget.classList.remove("hidden")
  }

  syncFieldValue() {
    if (this.editorElement?.getModelValue) {
      const value = this.editorElement.getModelValue()
      if (typeof value === "string") {
        this.fieldTarget.value = value
        return
      }
    }
    this.fieldTarget.value = this.fallbackTarget.value
  }

  // 타이핑 도중 매 글자마다 다시 그리면 화면이 깜빡인다 — 잠시 멈췄을 때만 그린다
  schedulePreview() {
    if (this.previewTimer) clearTimeout(this.previewTimer)
    this.previewTimer = setTimeout(() => this.renderPreview(), this.constructor.PREVIEW_DEBOUNCE_MS)
  }

  renderPreview() {
    if (!this.hasPreviewTarget) return

    this.syncFieldValue()
    this.previewTarget.srcdoc = this.buildPreviewDocument(this.fieldTarget.value)

    if (this.hasPreviewStatusTarget) {
      this.previewStatusTarget.textContent = "입력칸은 값이 비어 있습니다"
    }
  }

  buildPreviewDocument(body) {
    const styleSheetLinks = [...document.querySelectorAll('link[rel="stylesheet"]')]
      .map((link) => `<link rel="stylesheet" href="${link.getAttribute("href")}">`)
      .join("")

    return [
      "<!doctype html><html><head><meta charset=\"utf-8\">",
      styleSheetLinks,
      "<style>body{margin:0;padding:16px;background:#f3f4f6;}",
      ".gfm-markdown-page{width:auto;max-width:21cm;margin:0 auto;background:#fff;}</style>",
      "</head><body>",
      `<div class="${this.stylesheetHintValue}">${this.applyStyleTokens(body)}</div>`,
      "</body></html>",
    ].join("")
  }

  // 서버(StaffHelper)와 같은 치환을 한다 — 규칙 자체는 서버에서 받아온 것이다
  applyStyleTokens(body) {
    let result = String(body ?? "")
    for (const [token, style] of Object.entries(this.styleTokensValue)) {
      result = result.split(token).join(style)
    }
    return result
  }
}
